import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, TextInput, Keyboard, ScrollView } from 'react-native';
import { systemAlert } from 'shared/src/component';
import {
  Flex,
  StackScreen,
  PrimaryButton,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useRequestAuthCode } from '../queries/useRequestAuthCode';
import type { VerificationCodeResponse } from 'shared/src/api/myPageService/phoneAuthPoc/requestAuthCode/types';
import { useValidateAuthCode } from '../queries/useValidateAuthCode';
import { isAndroid } from 'shared/src/utils';
import Toast from 'react-native-toast-message';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Props = {
  visible: boolean;
  onClose: () => void;
  onVerificationComplete: () => void;
};

export const VerificationModal = ({
  visible,
  onClose,
  onVerificationComplete,
}: Props) => {
  const { width } = useScreenSize();
  const insets = useSafeAreaInsets();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [publicCode, setPublicCode] = useState<string>('');
  const [codeReceivedPhone, setCodeReceivedPhone] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0); // 초 단위 (180 = 3분)

  const timerRef = useRef<number | null>(null);

  const { mutateAsync: requestAuthCode } = useRequestAuthCode();
  const { mutateAsync: validateAuthCode } = useValidateAuthCode();

  /** 전화번호 입력 */
  const onChangePhone = (text: string) => {
    const onlyNumber = text.replace(/\D/g, '').slice(0, 11);
    setPhone(onlyNumber);
  };

  /** 인증번호 입력 */
  const onChangeCode = (text: string) => {
    const onlyNumber = text.replace(/\D/g, '').slice(0, 4);
    setCode(onlyNumber);
  };

  /** 전화번호 유효성 */
  const isPhoneValid = useCallback(() => phone.length >= 10, [phone]);

  /** 인증번호 유효성 */
  const isCodeValid = useCallback(
    () => code.length === 4 && timeLeft > 0,
    [code, timeLeft],
  );

  /** 타이머 시작 */
  const startTimer = () => {
    // 기존 타이머 제거
    clearTimer();

    // 타이머 시작
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  /** 기존 타이머 제거 */
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current!);
      timerRef.current = null;
    }
  };

  /** 인증번호 요청 */
  const handleRequestCode = async () => {
    if (!isPhoneValid()) return;
    Keyboard.dismiss();

    try {
      const res: VerificationCodeResponse = await requestAuthCode({
        phone_number: phone,
      });

      setCodeReceivedPhone(phone);
      setPublicCode(res?.data.public_code);
      setTimeLeft(res?.data.remain_seconds);
      setIsCodeSent(true);
      setCode('');
      startTimer();
    } catch (ex) {
      const error = ex as Error;
      Keyboard.dismiss();
      setTimeout(() => {
        Toast.show({
          type: 'error',
          text1: error.message,
          position: 'bottom',
        });
      }, 500);
    }
  };

  /** 인증번호 검증 요청 */
  const handleValidateCode = async () => {
    if (!isCodeValid() || !publicCode.trim()) return;

    // 인증 요청 시점의 번호와 현재 입력된 번호가 다를 경우
    if (phone !== codeReceivedPhone) {
      systemAlert(
        '',
        `인증 요청된 번호와 현재 입력된 번호가 불일치합니다.\n기존 번호(${codeReceivedPhone})로 인증하시겠습니까?`,
        [
          {
            text: '취소',
            style: 'cancel',
          },
          {
            text: '인증하기',
            onPress: async () => {
              // 입력값을 기존 인증요청 번호로 되돌림
              setPhone(codeReceivedPhone);
              await requestValidateAuthCode();
            },
          },
        ],
      );
      return;
    }
    await requestValidateAuthCode();
  };

  const requestValidateAuthCode = async () => {
    try {
      await validateAuthCode({ auth_code: code, public_code: publicCode });
      onVerificationComplete();
    } catch (ex) {
      const error = ex as Error;
      Keyboard.dismiss();
      setTimeout(() => {
        Toast.show({
          type: 'error',
          text1: error.message,
          position: 'bottom',
        });
      }, 500);
    }
  };

  /** 시간 표시 포맷 */
  const formattedTime = useCallback(() => {
    const min = Math.floor(timeLeft / 60);
    const sec = (timeLeft % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  }, [timeLeft]);

  useEffect(() => {
    if (visible) {
      // 상태 초기화
      setPhone('');
      setCode('');
      setPublicCode('');
      setIsCodeSent(false);
      setTimeLeft(0);
    }
    return () => {
      // 기존 타이머 정리
      clearTimer();
    };
  }, [visible]);

  return (
    <Modal
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}
      transparent={false}
      statusBarTranslucent={isAndroid}
    >
      <StackScreen
        title="휴대폰 번호 인증"
        onPressBack={onClose}
        bottom={{
          element: (
            <Flex padding={{ horizontal: 16, bottom: insets.bottom + 16 }}>
              <PrimaryButton
                variant="fill"
                color="pink"
                size="xlarge"
                state={isCodeValid() ? undefined : 'disabled'}
                onPress={handleValidateCode}
              >
                휴대폰 번호 인증하기
              </PrimaryButton>
            </Flex>
          ),
          avoidKeyboard: true,
          additionalKeyboardOffset: 0,
        }}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            width,
            marginHorizontal: 'auto',
          }}
          keyboardShouldPersistTaps="always"
        >
          <Flex padding={{ horizontal: 16, top: 18 }}>
            <Typography
              type="subtitle1"
              color="content_secondary"
              marginBottom={10}
            >
              휴대폰 번호를 인증해주세요
            </Typography>

            <Flex.Row alignItems="center" gap={12}>
              <Flex flex={1}>
                <TextInput
                  value={phone}
                  onChangeText={onChangePhone}
                  placeholder="휴대폰 번호 (숫자만 입력)"
                  keyboardType="number-pad"
                  placeholderTextColor="#B0B0B0"
                  style={{
                    paddingVertical: 12,
                    fontSize: 16,
                    borderBottomWidth: 1,
                    borderBottomColor: '#E6E6E6',
                    color: '#111',
                  }}
                />
              </Flex>

              <PrimaryButton
                size="large"
                outline
                state={isPhoneValid() ? undefined : 'disabled'}
                onPress={handleRequestCode}
              >
                {isCodeSent ? '다시받기' : '인증번호'}
              </PrimaryButton>
            </Flex.Row>

            {isCodeSent && (
              <>
                <TextInput
                  value={code}
                  onChangeText={onChangeCode}
                  placeholder="인증번호 입력"
                  keyboardType="number-pad"
                  placeholderTextColor="#B0B0B0"
                  textContentType="oneTimeCode"
                  style={{
                    marginTop: 10,
                    paddingVertical: 12,
                    fontSize: 16,
                    borderBottomWidth: 1,
                    borderBottomColor: '#E6E6E6',
                    color: '#111',
                  }}
                />

                <Flex padding={{ top: 12 }}>
                  <Typography type="body2" color="content_pink_primary">
                    {timeLeft === 0
                      ? '인증 시간이 만료되었어요'
                      : `남은 시간 ${formattedTime()}`}
                  </Typography>
                </Flex>
              </>
            )}
          </Flex>
        </ScrollView>
      </StackScreen>
      <Toast />
    </Modal>
  );
};
